import Email from "@/domain/entities/emails/Email";
import useSttCorrectionEnabled from "@/hooks/useSttCorrectionEnabled";
import {
    SttEmailRecipientType,
    useSdk,
    type SttEmailRecipient,
    type SttMailInput,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useCallback } from "react";

// Mappe les destinataires d'un type donné vers le format attendu par le SDK STT
const mapRecipients = (
    users: Email["to"] | undefined,
    type: SttEmailRecipientType,
): SttEmailRecipient[] =>
    (users ?? []).map((u) => ({ name: u.name, email: u.email, type }));

/**
 * Apprentissage du dictionnaire STT depuis les emails lus.
 *
 * Retourne une fonction fire-and-forget : à l'ouverture d'un mail, on extrait
 * les noms propres (expéditeur, destinataires, entités) pour enrichir le
 * dictionnaire de correction vocale. Les échecs sont silencieux — c'est un
 * effet de bord non bloquant pour la lecture du mail.
 */
const useSttExtractFromEmail = () => {
    const { sttManager } = useSdk();

    // Apprentissage gaté sur le MÊME toggle que la correction : tant qu'il est OFF,
    // aucun contenu de mail (sujet/corps/destinataires) ne quitte le client pour une
    // fonctionnalité dormante (privacy + perf).
    const isSttCorrectionEnabled = useSttCorrectionEnabled();

    return useCallback(
        (mail: Email): void => {
            if (!isSttCorrectionEnabled) return;

            const input: SttMailInput = {
                subject: mail.subject,
                body: mail.uniqueBody || mail.message,
                fromName: mail.from?.name ?? "",
                fromEmail: mail.from?.email ?? "",
                recipients: [
                    ...mapRecipients(mail.to, SttEmailRecipientType.To),
                    ...mapRecipients(mail.cc, SttEmailRecipientType.Cc),
                    ...mapRecipients(mail.bcc, SttEmailRecipientType.Bcc),
                ],
            };

            sttManager.extractFromEmail(input).catch(() => {
                // Apprentissage best-effort : on n'interrompt pas la lecture du mail
            });
        },
        [sttManager, isSttCorrectionEnabled],
    );
};

export default useSttExtractFromEmail;
